import AmountCell from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/AmountCell';
import formatQuantity from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/Helpers/formatQuantity';
import formatTokens from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/Helpers/formatTokens';
import resolveSubjectLabel from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/Helpers/resolveSubjectLabel';
import NameCell from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/NameCell';
import PartCostCell from '@UI/Pages/Administration/AiCosts/CostTabs/SubjectCosts/PartCostCell';
import resolveDrillAction from '@UI/Pages/Administration/AiCosts/CostTabs/SubjectCosts/Helpers/resolveDrillAction';
import subjectDimensionLabels, {
	type SubjectDimension,
} from '@UI/Pages/Administration/AiCosts/CostTabs/SubjectCosts/Helpers/subjectDimensionLabels';
import LimitCell from '@UI/Pages/Administration/AiCosts/CostTabs/SubjectCosts/LimitCell';
import type AiCostLimitSubjectsView from '@UI/Store/AiCostLimits/Views/AiCostLimitSubjectsView';
import type AiCostLimitsListView from '@UI/Store/AiCostLimits/Views/AiCostLimitsListView';
import formatEuros from '@UI/Helpers/formatEuros';
import formatAudioDuration from '@UI/Pages/Administration/AiCosts/Helpers/formatAudioDuration';
import formatCount from '@UI/Pages/Administration/AiCosts/Helpers/formatCount';
import type AiCostBreakdownListView from '@UI/Store/AiCosts/Views/AiCostBreakdownListView';
import type AiCostBreakdownRowView from '@UI/Store/AiCosts/Views/AiCostBreakdownRowView';
import type AiCostSummaryView from '@UI/Store/AiCosts/Views/AiCostSummaryView';
import { theme } from '@Guidelines/Theme/theme';
import costTableStyles from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/costTableStyles';
import { css } from '@emotion/css';
import { ChevronRight } from 'lucide-react';
import * as React from 'react';
import type { ComponentProps } from 'react';
import { Button, Column, type DataTableRowEvent, DataTable } from '@verclam/ui-kit';

const cellStyle = { paddingInline: '0.375rem' };

/** Tighter cells so the ten columns fit the card; the kit wrapper still scrolls sideways when they do not. */
const subjectTablePt: ComponentProps<typeof DataTable>['pt'] = {
	headerCell: { style: cellStyle },
	bodyCell: { style: cellStyle },
	footerCell: {
		style: { ...cellStyle, ...costTableStyles.pt.footerCell.style },
	},
};

const drillableRowClass = css`
	cursor: pointer;
`;

type Props = {
	readonly list: AiCostBreakdownListView;
	readonly dimension: SubjectDimension;
	readonly total: AiCostSummaryView | null;
	readonly reference: AiCostSummaryView | null;
	/** Both `null` when the limit column is not offered. */
	readonly limits: AiCostLimitsListView | null;
	readonly subjects: AiCostLimitSubjectsView | null;
	readonly showsConsumption: boolean;
};

/** One line per facility, structure or user, with the Total of the filtered summary in the footer. */
const SubjectCostsTable: React.FC<Props> = ({
	list,
	dimension,
	total,
	reference,
	limits,
	subjects,
	showsConsumption,
}) => {
	const labels = subjectDimensionLabels[dimension];
	const firstRow = list.list[0];
	const hasTokens = (firstRow?.quantities ?? null) !== null;
	const hasCalls = (firstRow?.callCount ?? null) !== null;

	const renderSubject = React.useCallback(
		(row: AiCostBreakdownRowView) => {
			const subject = resolveSubjectLabel(dimension, row);

			return <NameCell title={subject.title} detail={subject.detail} isMuted={subject.isMuted} />;
		},
		[dimension],
	);

	const renderAudioCount = React.useCallback(
		(row: AiCostBreakdownRowView) => (
			<AmountCell
				label={formatCount(row.audio.count)}
				detail={formatAudioDuration(row.audio.durationSeconds)}
			/>
		),
		[],
	);

	const renderAudioCost = React.useCallback(
		(row: AiCostBreakdownRowView) => (
			<PartCostCell
				part={row.audio}
				unit="audio"
				referenceAverage={reference?.audio.averageCost ?? null}
				color={theme.aiCosts.audio.ink}
			/>
		),
		[reference],
	);

	const renderDocumentCount = React.useCallback(
		(row: AiCostBreakdownRowView) => <AmountCell label={formatCount(row.documents.count)} />,
		[],
	);

	const renderDocumentCost = React.useCallback(
		(row: AiCostBreakdownRowView) => (
			<PartCostCell
				part={row.documents}
				unit="document"
				referenceAverage={reference?.documents.averageCost ?? null}
				color={theme.aiCosts.documents.ink}
			/>
		),
		[reference],
	);

	const renderTokens = React.useCallback(
		(row: AiCostBreakdownRowView) => (
			<AmountCell label={formatTokens(row.quantities?.totalTokens ?? null)} />
		),
		[],
	);

	const renderCalls = React.useCallback(
		(row: AiCostBreakdownRowView) => <AmountCell label={formatCount(row.callCount)} />,
		[],
	);

	const renderTotal = React.useCallback(
		(row: AiCostBreakdownRowView) => (
			<AmountCell
				label={formatEuros(row.costAmount)}
				isBold
				detail={
					row.unpricedItemCount > 0 ? `hors ${formatCount(row.unpricedItemCount)} sans tarif` : null
				}
			/>
		),
		[],
	);

	const renderLimit = React.useCallback(
		(row: AiCostBreakdownRowView) => {
			if (limits === null || subjects === null) {
				return null;
			}

			return (
				<LimitCell
					row={row}
					dimension={dimension}
					limits={limits}
					subjects={subjects}
					showsConsumption={showsConsumption}
				/>
			);
		},
		[dimension, limits, subjects, showsConsumption],
	);

	const renderDrill = React.useCallback(
		(row: AiCostBreakdownRowView) => {
			const drill = resolveDrillAction(dimension, row, resolveSubjectLabel(dimension, row));

			if (drill === null) {
				return null;
			}

			return (
				<Button
					type="button"
					text
					rounded
					severity="secondary"
					icon={<ChevronRight size={16} />}
					aria-label={drill.ariaLabel}
					onClick={drill.run}
				/>
			);
		},
		[dimension],
	);

	const handleRowClick = React.useCallback(
		(event: DataTableRowEvent<AiCostBreakdownRowView>) => {
			resolveDrillAction(dimension, event.data, resolveSubjectLabel(dimension, event.data))?.run();
		},
		[dimension],
	);

	const resolveRowClassName = React.useCallback(
		(row: AiCostBreakdownRowView) =>
			resolveDrillAction(dimension, row, resolveSubjectLabel(dimension, row)) === null
				? undefined
				: drillableRowClass,
		[dimension],
	);

	return (
		<DataTable
			value={list.list}
			ariaLabel={labels.title}
			onRowClick={handleRowClick}
			rowClassName={resolveRowClassName}
			pt={subjectTablePt}
		>
			<Column
				header={labels.column}
				body={renderSubject}
				footer={
					total === null ? undefined : (
						<NameCell
							title="Total"
							detail={formatQuantity(list.totalRecords, labels.singular, labels.plural)}
						/>
					)
				}
			/>
			<Column
				header="Audios"
				headerClassName={costTableStyles.audioHeader}
				align="right"
				body={renderAudioCount}
				footer={
					total === null ? undefined : (
						<AmountCell
							label={formatCount(total.audio.count)}
							detail={formatAudioDuration(total.audio.durationSeconds)}
						/>
					)
				}
			/>
			<Column
				header="Coût audio"
				headerClassName={costTableStyles.audioHeader}
				align="right"
				body={renderAudioCost}
				footer={
					total === null ? undefined : (
						<AmountCell
							label={formatEuros(total.audio.costAmount)}
							color={theme.aiCosts.audio.ink}
							detail={
								total.audio.averageCost === null
									? null
									: `${formatEuros(total.audio.averageCost)} / audio`
							}
						/>
					)
				}
			/>
			<Column
				header="Documents"
				headerClassName={costTableStyles.documentsHeader}
				align="right"
				body={renderDocumentCount}
				footer={
					total === null ? undefined : <AmountCell label={formatCount(total.documents.count)} />
				}
			/>
			<Column
				header="Coût documents"
				headerClassName={costTableStyles.documentsHeader}
				align="right"
				body={renderDocumentCost}
				footer={
					total === null ? undefined : (
						<AmountCell
							label={formatEuros(total.documents.costAmount)}
							color={theme.aiCosts.documents.ink}
							detail={
								total.documents.averageCost === null
									? null
									: `${formatEuros(total.documents.averageCost)} / document`
							}
						/>
					)
				}
			/>
			<Column
				header="Jetons"
				align="right"
				hidden={!hasTokens}
				body={renderTokens}
				footer={
					total === null ? undefined : (
						<AmountCell label={formatTokens(total.quantities?.totalTokens ?? null)} />
					)
				}
			/>
			<Column
				header="Appels"
				align="right"
				hidden={!hasCalls}
				body={renderCalls}
				footer={total === null ? undefined : <AmountCell label={formatCount(total.callCount)} />}
			/>
			<Column
				header="Total"
				align="right"
				body={renderTotal}
				footer={
					total === null ? undefined : (
						<AmountCell
							label={formatEuros(total.costAmount)}
							isBold
							detail={
								total.unpricedItemCount > 0
									? `hors ${formatCount(total.unpricedItemCount)} sans tarif`
									: null
							}
						/>
					)
				}
			/>
			<Column header="Plafond" body={renderLimit} hidden={limits === null || subjects === null} />
			<Column header="" body={renderDrill} />
		</DataTable>
	);
};

export default SubjectCostsTable;
